import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import type { Lang } from '../offers/config';
import { IconMoney, IconCredit, IconAI, IconSoftware, IconRewards, IconBusiness } from './icons/BlinkIcons';

// ============================================================
// Las 6 tarjetas tenian cursor-pointer, hover lift y borde que se
// ilumina — parecian clickeables y no lo eran. Peor que un link
// roto: es una promesa visual falsa.
//
// Ahora solo Credit es interactiva, porque es la unica con pagina.
// Las otras cinco se muestran atenuadas con "Proximamente": dicen
// la verdad sobre su estado sin esconder la vision del proyecto.
//
// Cuando una vertical tenga contenido, le pones su `path` aqui y
// se vuelve clickeable sola.
// ============================================================
const SOON = {
  es: 'Proximamente',
  en: 'Coming soon'
};
export function Ecosystem() {
  const {
    t,
    language
  } = useLanguage();
  const lang = (language === 'en' ? 'en' : 'es') as Lang;
  const cards = [{
    key: 'money',
    title: t('ecosystem.cards.money.title'),
    icon: IconMoney,
    desc: t('ecosystem.cards.money.desc'),
    path: null
  }, {
    key: 'credit',
    title: t('ecosystem.cards.credit.title'),
    icon: IconCredit,
    desc: t('ecosystem.cards.credit.desc'),
    path: '/oferta/credito'
  }, {
    key: 'ai',
    title: t('ecosystem.cards.ai.title'),
    icon: IconAI,
    desc: t('ecosystem.cards.ai.desc'),
    path: null
  }, {
    key: 'software',
    title: t('ecosystem.cards.software.title'),
    icon: IconSoftware,
    desc: t('ecosystem.cards.software.desc'),
    path: null
  }, {
    key: 'rewards',
    title: t('ecosystem.cards.rewards.title'),
    icon: IconRewards,
    desc: t('ecosystem.cards.rewards.desc'),
    path: null
  }, {
    key: 'business',
    title: t('ecosystem.cards.business.title'),
    icon: IconBusiness,
    desc: t('ecosystem.cards.business.desc'),
    path: null
  }];
  const baseCard = 'glass-panel p-7 md:p-10 rounded-[2rem] border relative overflow-hidden flex flex-col justify-start min-h-[240px] h-full transition-all duration-300';
  const activeCard = 'group cursor-pointer border-white/5 md:hover:bg-surface/60 md:hover:border-accent/30 md:hover:-translate-y-[2px] active:scale-[0.98] shadow-[0_4px_24px_rgba(0,0,0,0.2)] md:hover:shadow-[0_12px_40px_rgba(255,106,0,0.15)]';
  const soonCard = 'border-white/[0.04] opacity-55 cursor-default';
  return <section className="py-20 md:py-32 relative z-10" id="ecosystem" data-id="element-36" data-mp="39tbf3m74qgo2">
      <div className="max-w-7xl mx-auto px-5 sm:px-6" data-id="element-37" data-mp="35shoum67sps0">
        <div className="mb-12 md:mb-24" data-id="element-38" data-mp="30sdk1teo5jhm">
          <h2 className="text-[40px] leading-[1.05] md:text-4xl font-display font-medium mb-4" data-id="element-39" data-mp="30rbxoss7dakl">
            {t('ecosystem.title')}
          </h2>
          <p className="text-white/50 text-[18px] leading-[1.5] md:text-lg max-w-2xl font-light" data-id="element-40" data-mp="2vvmso9xez1gw">
            {t('ecosystem.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8" data-id="element-41" data-mp="1g91berbev4eo">
          {cards.map((card, index) => {
          const live = Boolean(card.path);
          const inner = <>
                <div className="absolute inset-0 shadow-[inset_0_1px_1px_rgba(255,255,255,0.05)] rounded-[2rem] pointer-events-none" data-id="element-42" data-mp="1p8wtlg3dvzo7" />
                {live && <div className="absolute inset-0 bg-gradient-to-br from-accent/[0.02] to-transparent opacity-0 md:group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" data-id="element-43" data-mp="xkwea2p9ehou" />}

                <div className="flex items-start justify-between gap-3 mb-6 md:mb-8 relative z-10" data-id="element-44" data-mp="16dpx2a3wzz7g">
                  <div className={`w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] shrink-0 transition-transform duration-300 ${live ? 'md:group-hover:bg-white/10 md:group-hover:-translate-y-[2px]' : ''}`} data-id="element-45" data-mp="9tqzzryyvxgn">
                    <card.icon className={`w-6 h-6 text-white/60 transition-colors duration-300 ${live ? 'md:group-hover:text-white' : ''}`} data-id="element-46" data-mp="3lzd5ng8e1lwh" />
                  </div>

                  {!live && <span className="text-[11px] uppercase tracking-[0.12em] text-white/35 border border-white/10 rounded-full px-3 py-1 whitespace-nowrap" data-id="element-47" data-mp="dhgshyiefj1q">
                      {SOON[lang]}
                    </span>}
                </div>

                <h3 className={`text-[28px] md:text-2xl font-display font-medium mb-3 md:mb-4 text-white/90 transition-colors relative z-10 ${live ? 'md:group-hover:text-white' : ''}`} data-id="element-48" data-mp="nf4un9ic3stx">
                  {card.title}
                </h3>
                <p className="text-white/50 text-[17px] md:text-base leading-relaxed font-light relative z-10" data-id="element-49" data-mp="2k9ht3mjjivyc">
                  {card.desc}
                </p>
              </>;
          return <motion.div key={card.key} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} data-id="element-50" data-mp="14op7ypaiy0pv" data-mpi={index}>
                {live ? <Link to={card.path as string} className={`${baseCard} ${activeCard} block`} data-id="element-51" data-mp="h20o3y1yfy6m">
                    {inner}
                  </Link> : <div className={`${baseCard} ${soonCard}`} aria-disabled="true" data-id="element-52" data-mp="c01ngvxlnfdd">
                    {inner}
                  </div>}
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}